import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Baby, MessageCircle, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import { FadeInUp } from '../components/Animations';

const TherapyPage = () => {
  const services = [
    {
      icon: <Heart className="w-10 h-10" />,
      title: 'Individual Therapy',
      description: 'One-on-one support for anxiety, depression, trauma, life transitions, and personal growth.',
      approaches: ['CBT', 'ACT', 'Schema Therapy', 'Mindfulness-Based']
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Couples & Family Therapy',
      description: 'Support for relationships, communication challenges, and family dynamics.',
      approaches: ['Gottman Method', 'EFT', 'Solution-Focused']
    },
    {
      icon: <Baby className="w-10 h-10" />,
      title: 'Child & Adolescent Therapy',
      description: 'Age-appropriate support for young people facing emotional and behavioural challenges.',
      approaches: ['Play Therapy', 'CBT', 'Parent Coaching']
    },
    {
      icon: <MessageCircle className="w-10 h-10" />,
      title: 'Parent Support',
      description: 'Guidance for parents navigating parenting challenges, neurodivergent children, and family stress.',
      approaches: ['Psychoeducation', 'Behavioural Strategies', 'Coping Skills']
    }
  ];

  const conditions = [
    'Anxiety and panic disorders',
    'Depression and mood disorders',
    'Trauma and PTSD',
    'Stress and burnout',
    'Relationship difficulties',
    'Life transitions and adjustment',
    'Grief and loss',
    'Self-esteem and identity issues',
    'Parenting challenges',
    'Supporting neurodivergent individuals',
    'Substance use and addiction',
    'Eating disorders',
    'Behavioural difficulties'
  ];

  return (
    <>
      <SEO
        title="Online Therapy Services - Telehealth Counselling for All Ages"
        description="Evidence-based online therapy for individuals, couples, and families. Professional telehealth counselling for anxiety, depression, trauma, and more across Australia."
        keywords="online therapy, telehealth therapy, online counselling, anxiety therapy, depression therapy, trauma therapy, couples therapy, child therapy, psychology services"
        url="/therapy"
      />

      {/* Hero Section */}
      <section className="hero-gradient py-16 md:py-20 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                <span style={{ color: 'var(--blue-primary)' }}>Therapy Services</span> Online
              </h1>
              <p className="text-lg md:text-xl mb-4" style={{ color: 'var(--text-secondary)' }}>
                Evidence-based telehealth therapy for individuals, couples, and families
              </p>
              <p className="text-base" style={{ color: 'var(--text-muted)' }}>
                Accessible mental health support across Australia
              </p>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Referral Pathways Section */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center" style={{ color: 'var(--blue-primary)' }}>
                Referral Pathways
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
                {[
                  'Mental Health Care Plans',
                  'Private & Self-Funded',
                  'NDIS',
                  'DVA',
                  'WorkCover & EAP'
                ].map((pathway, index) => (
                  <FadeInUp key={pathway} delay={index * 0.1}>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-full shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 flex items-center justify-center min-h-[140px]">
                      <p className="text-center font-semibold text-sm md:text-base" style={{ color: 'var(--navy-primary)' }}>
                        {pathway}
                      </p>
                    </div>
                  </FadeInUp>
                ))}
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Services Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                Our Therapy Services
              </h2>
              <p className="text-lg max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
                Tailored support using evidence-based approaches
              </p>
            </div>
          </FadeInUp>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <FadeInUp key={service.title} delay={index * 0.1}>
                <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--blue-pale)] rounded-full mb-4" style={{ color: 'var(--navy-primary)' }}>
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                    {service.title}
                  </h3>
                  <p className="mb-4 text-lg" style={{ color: 'var(--text-secondary)' }}>
                    {service.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {service.approaches.map((approach) => (
                      <span
                        key={approach}
                        className="px-3 py-1 bg-[var(--gray-100)] text-sm rounded-full"
                        style={{ color: 'var(--navy-primary)' }}
                      >
                        {approach}
                      </span>
                    ))}
                  </div>
                </div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* Conditions We Support Section */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <FadeInUp>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center" style={{ color: 'var(--text-primary)' }}>
                Conditions We Support
              </h2>
              <p className="text-lg mb-8 text-center" style={{ color: 'var(--text-secondary)' }}>
                We provide therapy for a wide range of mental health concerns
              </p>
            </FadeInUp>

            <FadeInUp delay={0.2}>
              <div className="bg-white p-8 rounded-xl shadow-md">
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">
                  {conditions.map((condition) => (
                    <div key={condition} className="flex items-center">
                      <svg className="w-5 h-5 mr-3 flex-shrink-0" style={{ color: 'var(--blue-primary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span style={{ color: 'var(--text-secondary)' }}>{condition}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* How Therapy Works Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <FadeInUp>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                How Telehealth Therapy Works
              </h2>
              <div className="space-y-4 text-lg" style={{ color: 'var(--text-secondary)' }}>
                <p>
                  Our telehealth therapy sessions are conducted via secure video conferencing, providing the same quality care as traditional in-person therapy.
                </p>
                <div className="bg-[var(--gray-100)] p-6 rounded-xl mt-6">
                  <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>What to expect:</h3>
                  <ol className="space-y-3">
                    <li className="flex items-start">
                      <span className="font-bold mr-3" style={{ color: 'var(--blue-primary)' }}>1.</span>
                      <div>
                        <strong>Initial Consultation:</strong> We'll discuss your concerns, goals, and determine if we're a good fit.
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="font-bold mr-3" style={{ color: 'var(--blue-primary)' }}>2.</span>
                      <div>
                        <strong>Regular Sessions:</strong> Typically 50 minutes, scheduled at times that work for you.
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="font-bold mr-3" style={{ color: 'var(--blue-primary)' }}>3.</span>
                      <div>
                        <strong>Collaborative Approach:</strong> We'll work together on goals using evidence-based techniques.
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="font-bold mr-3" style={{ color: 'var(--blue-primary)' }}>4.</span>
                      <div>
                        <strong>Progress Monitoring:</strong> Regular check-ins to ensure therapy is meeting your needs.
                      </div>
                    </li>
                  </ol>
                </div>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* Benefits of Online Therapy Section */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <FadeInUp>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center" style={{ color: 'var(--text-primary)' }}>
                Benefits of Online Therapy
              </h2>
            </FadeInUp>

            <div className="grid md:grid-cols-2 gap-6 mt-8">
              {[
                { title: 'Accessible', description: 'No travel required—attend from anywhere in Australia' },
                { title: 'Comfortable', description: 'Receive support in your own familiar environment' },
                { title: 'Flexible', description: 'Schedule sessions that fit your lifestyle' },
                { title: 'Effective', description: 'Research shows online therapy is as effective as in-person' }
              ].map((benefit, index) => (
                <FadeInUp key={benefit.title} delay={index * 0.1}>
                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                      {benefit.title}
                    </h3>
                    <p style={{ color: 'var(--text-secondary)' }}>
                      {benefit.description}
                    </p>
                  </div>
                </FadeInUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="section">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center" style={{ color: 'var(--text-primary)' }}>
                Related Services
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Link to="/asd-assessment" className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow group">
                  <h3 className="text-lg font-bold mb-2 flex items-center" style={{ color: 'var(--text-primary)' }}>
                    ASD Assessment
                    <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--blue-primary)' }} />
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Comprehensive autism spectrum assessments for adults and children via telehealth. Neurodiversity-affirming approach.
                  </p>
                </Link>
                <Link to="/adhd-assessment" className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow group">
                  <h3 className="text-lg font-bold mb-2 flex items-center" style={{ color: 'var(--text-primary)' }}>
                    ADHD Assessment
                    <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--blue-primary)' }} />
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Evidence-based ADHD assessments for all ages. Detailed reports and ongoing support available.
                  </p>
                </Link>
                <Link to="/contact" className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow group">
                  <h3 className="text-lg font-bold mb-2 flex items-center" style={{ color: 'var(--text-primary)' }}>
                    Get in Touch
                    <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--blue-primary)' }} />
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Have questions about therapy or need help choosing the right service? Contact us — no obligation.
                  </p>
                </Link>
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="bg-[var(--blue-primary)] text-white rounded-3xl p-8 md:p-12 text-center max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Start Therapy?
              </h2>
              <p className="text-lg mb-8 opacity-90">
                Take the first step towards positive change and wellbeing
              </p>
              <Link
                to="/contact"
                data-testid="therapy-cta-button"
                className="px-8 py-4 bg-white text-[var(--blue-primary)] rounded-full font-medium hover:bg-[var(--gray-100)] transition-all duration-300 hover:scale-105 inline-block"
              >
                Book a Session
              </Link>
            </div>
          </FadeInUp>
        </div>
      </section>
    </>
  );
};

export default TherapyPage;